import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

// MD
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDButton from 'components/MDButton'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Services
import { useAppServices } from 'hook/services'

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  paused: 'default',
}
const TIER_COLORS = {
  featured: 'warning',
  listed: 'info',
}

const initials = (first = '', last = '') =>
  `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase() || '?'

const safeUrl = (url) => {
  if (!url) return ''
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function CreativeDirectoryDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const AppService = useAppServices()

  const [listing, setListing] = useState(null)
  const [loader, setLoader] = useState(true)
  const [working, setWorking] = useState(null) // 'approve' | 'reject' | 'pause' | null
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const load = async () => {
    setLoader(true)
    const { response } = await AppService.creativeDirectory.getByIdAdmin({
      params: { id },
    })
    if (response?.success) setListing(response.data)
    else setListing(null)
    setLoader(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleApprove = async () => {
    setWorking('approve')
    const { response, error } = await AppService.creativeDirectory.approve({
      payload: { listingId: id },
    })
    setWorking(null)
    if (response?.success) load()
    else alert(response?.message || error?.message || 'Could not approve listing.')
  }

  const submitReject = async () => {
    setWorking('reject')
    const { response, error } = await AppService.creativeDirectory.reject({
      payload: { listingId: id, reason: rejectReason },
    })
    setWorking(null)
    setRejectOpen(false)
    setRejectReason('')
    if (response?.success) load()
    else alert(response?.message || error?.message || 'Could not reject listing.')
  }

  const handleTogglePause = async () => {
    const goingToPause = listing.status !== 'paused'
    setWorking('pause')
    const { response, error } = await AppService.creativeDirectory.adminPause({
      payload: { listingId: id, paused: goingToPause },
    })
    setWorking(null)
    if (response?.success) load()
    else
      alert(
        response?.message ||
        error?.message ||
        `Could not ${goingToPause ? 'pause' : 'unpause'} listing.`
      )
  }

  if (loader) {
    return (
      <DashboardLayout>
        <Loader />
      </DashboardLayout>
    )
  }

  if (!listing) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <Card>
            <MDBox p={4} textAlign="center">
              <MDTypography variant="h6">Listing not found.</MDTypography>
              <MDBox mt={2}>
                <MDButton
                  color="info"
                  variant="gradient"
                  onClick={() => navigate('/agency/creative-directory')}
                >
                  Back to directory
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    )
  }

  const portfolio = Array.isArray(listing.portfolioImages)
    ? listing.portfolioImages
    : []

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={4}>
        <Grid container spacing={3}>
          {/* Header card */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ md: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/agency/creative-directory')}>
                      <ArrowBackIcon />
                    </IconButton>
                    <Avatar
                      src={listing.user?.profile_image || portfolio[0] || ''}
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: '#FFEEDF',
                        color: '#835414',
                        fontWeight: 700,
                      }}
                    >
                      {initials(listing.firstName, listing.lastName)}
                    </Avatar>
                    <MDBox>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <MDTypography variant="h5">
                          {listing.firstName} {listing.lastName}
                        </MDTypography>
                        <Chip
                          label={listing.tier}
                          size="small"
                          color={TIER_COLORS[listing.tier] || 'default'}
                          sx={{ textTransform: 'uppercase', fontSize: 10 }}
                        />
                        <Chip
                          label={listing.status}
                          size="small"
                          color={STATUS_COLORS[listing.status] || 'default'}
                          sx={{ textTransform: 'uppercase', fontSize: 10 }}
                        />
                      </Stack>
                      <MDTypography variant="caption" color="text" component="div">
                        {Array.isArray(listing.professions) && listing.professions.length
                          ? listing.professions.join(', ')
                          : listing.profession} ·{' '}
                        {Array.isArray(listing.cities) && listing.cities.length
                          ? listing.cities.join(', ')
                          : listing.city}
                        {listing.studioName ? ` · ${listing.studioName}` : ''}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton
                      color="success"
                      variant="gradient"
                      startIcon={<CheckIcon />}
                      disabled={working === 'approve' || listing.status === 'approved'}
                      onClick={handleApprove}
                    >
                      {working === 'approve' ? 'Approving…' : 'Approve'}
                    </MDButton>
                    {/* Pause / Unpause — only meaningful once a listing is live
                        (approved or already paused). For pending/rejected it
                        is hidden so the admin isn't given an action that
                        would short-circuit the moderation flow. */}
                    {(listing.status === 'approved' || listing.status === 'paused') && (
                      <MDButton
                        color="warning"
                        variant="outlined"
                        startIcon={
                          listing.status === 'paused' ? <PlayArrowIcon /> : <PauseIcon />
                        }
                        disabled={working === 'pause'}
                        onClick={handleTogglePause}
                      >
                        {working === 'pause'
                          ? 'Saving…'
                          : listing.status === 'paused'
                            ? 'Unpause'
                            : 'Pause'}
                      </MDButton>
                    )}
                    <MDButton
                      color="error"
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      disabled={working === 'reject' || listing.status === 'rejected'}
                      onClick={() => setRejectOpen(true)}
                    >
                      Reject
                    </MDButton>
                  </MDBox>
                </Stack>
              </MDBox>
            </Card>
          </Grid>

          {/* Main column */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={1}>
                  About
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ whiteSpace: 'pre-line' }}>
                  {listing.bio || '—'}
                </MDTypography>

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" mb={2}>
                  Portfolio ({portfolio.length})
                </MDTypography>
                {portfolio.length === 0 ? (
                  <MDTypography variant="caption" color="text">
                    No portfolio images uploaded.
                  </MDTypography>
                ) : (
                  <Grid container spacing={1.5}>
                    {portfolio.map((src, i) => (
                      <Grid item xs={6} sm={4} md={3} key={`${src}-${i}`}>
                        <a href={src} target="_blank" rel="noopener noreferrer">
                          <MDBox
                            component="img"
                            src={src}
                            alt={`portfolio-${i + 1}`}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              objectFit: 'cover',
                              borderRadius: 1.5,
                              border: '1px solid #eee',
                              cursor: 'zoom-in',
                            }}
                          />
                        </a>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {listing.tier === 'featured' && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <MDTypography variant="h6" mb={1}>
                      Showreel link
                    </MDTypography>
                    {listing.reelLink ? (
                      <a
                        href={safeUrl(listing.reelLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {listing.reelLink}
                      </a>
                    ) : (
                      <MDTypography variant="caption" color="text">
                        No reel link.
                      </MDTypography>
                    )}
                  </>
                )}

                {listing.status === 'rejected' && listing.rejectionReason && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <MDBox p={2} sx={{ bgcolor: '#FFF1F1', borderRadius: 1 }}>
                      <MDTypography variant="button" fontWeight="medium" color="error">
                        Rejection reason
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        {listing.rejectionReason}
                      </MDTypography>
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Submission details
                </MDTypography>

                <DetailRow label="Tier" value={listing.tier} uppercase />
                <DetailRow label="Status" value={listing.status} uppercase />
                <DetailRow
                  label="Available"
                  value={listing.available ? 'Yes' : 'No'}
                />
                <DetailRow
                  label="Submitted"
                  value={
                    listing.createdAt
                      ? new Date(listing.createdAt).toLocaleString()
                      : '—'
                  }
                />



                <Divider sx={{ my: 2 }} />

                <MDTypography variant="h6" mb={2}>
                  Owner
                </MDTypography>
                <DetailRow
                  label="Email"
                  value={listing.user?.email || '—'}
                />
                <DetailRow
                  label="User name"
                  value={
                    listing.user
                      ? `${listing.user.first_name || ''} ${listing.user.last_name || ''}`.trim() ||
                      '—'
                      : '—'
                  }
                />
                {listing.user?.phone && (
                  <DetailRow label="Phone" value={listing.user.phone} />
                )}

                <Divider sx={{ my: 2 }} />

                <MDTypography variant="h6" mb={2}>
                  Links
                </MDTypography>
                {listing.website ? (
                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <Tooltip title="Open website">
                      <IconButton
                        size="small"
                        component="a"
                        href={safeUrl(listing.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <MDTypography variant="caption">{listing.website}</MDTypography>
                  </MDBox>
                ) : (
                  <MDTypography variant="caption" color="text">
                    No website provided.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reject {listing.firstName} {listing.lastName}'s listing
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" component="div" sx={{ mb: 2 }}>
            Optional — share a short reason. The creative will see this when
            they revisit their listing.
          </MDTypography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setRejectOpen(false)} color="secondary" variant="text">
            Cancel
          </MDButton>
          <MDButton
            onClick={submitReject}
            color="error"
            variant="gradient"
            disabled={working === 'reject'}
          >
            {working === 'reject' ? 'Rejecting…' : 'Reject listing'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

function DetailRow({ label, value, uppercase }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
      <MDTypography
        variant="caption"
        fontWeight="medium"
        sx={{ textTransform: uppercase ? 'uppercase' : 'none', textAlign: 'right', ml: 2 }}
      >
        {value}
      </MDTypography>
    </MDBox>
  )
}

export default CreativeDirectoryDetail
